import { VStack } from '@chakra-ui/react';
import type { Dispatch } from 'react';
import type { SelectOption, EditorProps } from '@kula-ai/components';
import { EmailBlockSection } from '../../../components/EmailBlockSection';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import { IcsAttachmentCheckbox } from '../../../components/IcsAttachmentCheckbox';
import type {
  InterviewCommunicationAction,
  InterviewCommunicationFormState,
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../../../InterviewActivity/communication/communication.types';
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/communication.validate';
import { SCHEDULE_INTERVIEW_COPY } from '../../schedule.copy';

interface CandidatePaneProps {
  state: InterviewCommunicationFormState;
  dispatch: Dispatch<InterviewCommunicationAction>;
  fieldErrors: InterviewCommunicationValidationErrors;
  templateOptions: SelectOption[];
  fromOptions: SelectOption[];
  ccBccOptions: SelectOption[];
  calendarVariables: InterviewVariablesByCategory;
  emailVariables: InterviewVariablesByCategory;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
}

export const CandidatePane = ({
  state,
  dispatch,
  fieldErrors,
  templateOptions,
  fromOptions,
  ccBccOptions,
  calendarVariables,
  emailVariables,
  uploadInlineImage,
}: CandidatePaneProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.communication;

  return (
    <VStack align="stretch" spacing="4" flex="1" minW={0}>
      <EmailBlockSection
        title={copy.candidate.calendarTitle}
        sectionId="schedule-comm-candidate-calendar"
      >
        <TemplatedEmail
          className="w-full"
          showFrom={false}
          showTo={false}
          showSubject={false}
          enableCcBcc={false}
          showTemplate
          showEditor
          editorVariant="calendarInvite"
          variables={calendarVariables}
          templateOptions={templateOptions}
          applyTemplateBodyToEditor
          value={state.candidateCalendar}
          onChange={(value) =>
            dispatch({ type: 'SET_CANDIDATE_CALENDAR', payload: value })
          }
          errors={fieldErrors.candidateCalendar}
          placeholders={{ template: copy.placeholders.template }}
        />
      </EmailBlockSection>

      <EmailBlockSection
        title={copy.candidate.emailTitle}
        sectionId="schedule-comm-candidate-email"
        showSwitch
        switchChecked={state.candidateEmailEnabled}
        onSwitchChange={(checked) =>
          dispatch({ type: 'SET_CANDIDATE_EMAIL_ENABLED', payload: checked })
        }
      >
        {state.candidateEmailEnabled ? (
          <>
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo={false}
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              variables={emailVariables}
              attachments={state.candidateEmailAttachments}
              onAttachmentsChange={(items: InterviewEditorAttachmentItem[]) =>
                dispatch({
                  type: 'SET_CANDIDATE_EMAIL_ATTACHMENTS',
                  payload: items,
                })
              }
              templateOptions={templateOptions}
              fromOptions={fromOptions}
              ccBccOptions={ccBccOptions}
              applyTemplateBodyToEditor
              value={state.candidateEmail}
              onChange={(value) =>
                dispatch({ type: 'SET_CANDIDATE_EMAIL', payload: value })
              }
              errors={fieldErrors.candidateEmail}
              placeholders={{
                from: copy.placeholders.from,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
            />
            <IcsAttachmentCheckbox
              checked={state.candidateIcsEnabled}
              onChange={(v) =>
                dispatch({ type: 'SET_CANDIDATE_ICS_ENABLED', payload: v })
              }
            />
          </>
        ) : null}
      </EmailBlockSection>
    </VStack>
  );
};
